import {hexToBase64, base64ToHex, reverseBytes, fromSatoshis} from "./chain-utils";

export const DUST_LIMIT = 546;
export const MIN_FEE = 1000; // BCHD doesn't broadcast TX that are below this

export interface BchdRestOutput {
    outpoint: {
        hash: string; // bas64 string
        index: number;
    },
    pubkey_script: string; // bas64 string
    value: string; // integer as string = sats
    is_coinbase: boolean;
    block_height: number;
    slp_token?: {
        token_id: string; // bas64 string
        amount: string; // integer as string
        is_mint_baton: boolean;
        address: string;
        decimals: number;
        slp_action: string; // NON_SLP
        token_type: number
    }
}
export interface BchdTokenMetadata {
    token_id: string; // bas64 string
    token_type: number;
    type1?: SlpTokenMeta;
    nft1_group?: SlpTokenMeta;
    nft1_child?: SlpChildTokenMeta;
}
export interface SlpTokenMeta {
    token_ticker: string; // bas64 string
    token_name: string; // bas64 string
    token_document_url: string; // bas64 string
    token_document_hash: string; // bas64 string
    decimals: number;
    mint_baton_txid: string; // bas64 string
    mint_baton_vout: number;
}
export interface SlpChildTokenMeta {
    token_ticker: string; // bas64 string
    token_name: string; // bas64 string
    token_document_url: string; // bas64 string
    token_document_hash: string; // bas64 string
    group_id: string; // bas64 string
}
export interface BchdRestUtxo {
    outputs: BchdRestOutput[];
    //token_metadata?: BchdTokenMetadata[]; // included if include_token_metadata === true
}

export interface BchUtxo {
    bchOutputs: BchdRestOutput[];
}

/**
 * A UTXO where outputs are seperated into 3 sets for BCH, SLP and SLP baton.
 * Always use this UTXO when sending SLP tokens to prevent
 * accidentally burning tokens.
 */
export interface SlpUtxo extends BchUtxo {
    slpOutputs: BchdRestOutput[];
    batonOutputs: BchdRestOutput[]; // minting baton UTXO
    token_metadata?: BchdTokenMetadata; // the metadata for this token
}

export class PromptRest {
    protected apiUrl: string = "";

    constructor(apiUrl: string = "") {
        if (apiUrl.length !== 0) {
            this.apiUrl = apiUrl;
        }
    }


    /**
     * Return the UTXO of an address.
     * Use getSlpAddressUnspentOutputs() to separate the UTXO into SLP outputs to avoid burning tokens.
     * @param address the CashAddr or SLP address
     * @param includeSlpMeta
     * @param includeMempool
     */
    public async getAddressUnspentOutputs(address: string, includeSlpMeta: boolean = false, includeMempool: boolean = false) {
        return new Promise<BchdRestUtxo>((resolve, reject) => {
            let url = this.apiUrl + "?method=GetAddressUnspentOutputs";
            let data = {
                address: address,
                include_mempool: includeMempool,
                include_token_metadata: includeSlpMeta
            }
            this.postApi(url, data).then((res: BchdRestUtxo) => {
                resolve(res);
            }).catch((err) => {
                reject({txt: "Invalid response when getting UTXO", err: err, address: address});
            });
        })
    }

    /**
     * Return the UTXO of a BCH address.
     * This function is SLP-aware, meaning it will filter any UTXOs that have SLP tokens associated with them
     * to prevent them from being burned accidentally.
     * @param address
     * @param includeMempool
     */
    public async getBchAddressUnspentOutputs(address: string, includeMempool: boolean = false): Promise<BchUtxo> {
        let utxo = await this.getAddressUnspentOutputs(address, true, includeMempool);
        return {
            // sort BCHD outputs ascending (smallest first)
            bchOutputs: utxo.outputs.filter(o => !o.slp_token || !o.slp_token.token_id).sort((a, b) => parseInt(a.value) - parseInt(b.value)),
        }
    }

    /**
     * Return the UTXO for an address
     * @param address an address as CashAddr or SLP address
     * @param tokenIdHex
     * @param includeMempool
     */
    public async getSlpAddressUnspentOutputs(address: string, tokenIdHex: string, includeMempool: boolean = false): Promise<SlpUtxo> {
        throw "Not Implemented";
        return null;
    }

    /**
     * Get the (unconfirmed) BCHD balance of the address.
     * If you specify a token ID it returns the balance of that token.
     * @param address
     * @param tokenID
     * @param includeMempool
     */
    public async getAddressBalance(address: string, tokenID: string = "", includeMempool: boolean = false): Promise<number> {
        let utxo = await this.getAddressUnspentOutputs(address, tokenID.length !== 0, includeMempool);
        if (utxo.outputs === undefined) // returns empty object for unused addresses
            return 0.0;
        if (tokenID == "")
            return this.readBchBalanceFromUtxo(utxo.outputs);
        return 0.0;
    }

    /**
     * Submit a transaction
     * @param txHex The transaction as hex string (as returned in bitcore.Transaction.toString() )
     * @return the transaction ID as hex
     */
    public async submitTransaction(txHex: string): Promise<string> {

        return new Promise<string>((resolve, reject)=>{
            reject("Not Implemented");
        })
    }

    // ################################################################
    // ###################### PRIVATE FUNCTIONS #######################

    protected readBchBalanceFromUtxo(outputs: BchdRestOutput[]): number {
        let sats = 0.0;
        outputs.forEach((output) => {
            const curSats = parseInt(output.value);
            if (curSats > DUST_LIMIT)
                sats += curSats;
        });
        return fromSatoshis(sats);
    }


    /**
     * Post JSON
     * @param url
     * @param data
     * @return the JSON response as JS object
     */
    protected async postApi(url: string, data?: any): Promise<any> {
        try {
            let res = await window.fetch(url, {
                method: "post",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data),
            });
            let json = await res.json();
            return json;
        }
        catch (err) {
            console.error("Error posting BCHD JSON", err);
            throw err;
        }
    }
}
